import { useEffect, useState } from "react";
import { RiCloseLine, RiFileLine, RiLoader4Line } from "react-icons/ri";

/**
 * Attachment chip shown in the comment composer (pending/existing) and under a posted comment.
 * Pass `file` for a not-yet-uploaded `File` — the chip manages its own preview object URL.
 * Pass `name`/`isImage`/`previewUrl` directly for an already-persisted attachment.
 */
const CommentAttachmentChip = ({
  file = null,
  name,
  isImage = false,
  previewUrl = null,
  onClick,
  onRemove,
  busy = false,
}) => {
  const [objectUrl, setObjectUrl] = useState(null);

  useEffect(() => {
    if (!file) {
      setObjectUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const displayName = name ?? file?.name ?? "Attachment";
  const displayIsImage = file ? Boolean(file.type?.startsWith("image/")) : isImage;
  const thumbnail = objectUrl ?? previewUrl;

  return (
    <div className="comment-attachment-chip">
      <button
        type="button"
        onClick={onClick}
        className="comment-attachment-chip-main"
        title={displayName}
        disabled={!onClick || busy}
      >
        {displayIsImage && thumbnail ? (
          <img src={thumbnail} alt={displayName} className="comment-attachment-chip-thumb" />
        ) : (
          <RiFileLine className="text-text-secondary shrink-0" />
        )}
        <span className="comment-attachment-chip-name">{displayName}</span>
      </button>
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          className="comment-attachment-chip-remove"
          aria-label={`Remove ${displayName}`}
        >
          {busy ? (
            <RiLoader4Line className="w-4 h-4 animate-spin" aria-hidden />
          ) : (
            <RiCloseLine className="w-4 h-4" aria-hidden />
          )}
        </button>
      ) : null}
    </div>
  );
};

export default CommentAttachmentChip;
